import { motion } from 'framer-motion';
import { ExternalLink, Share2, type LucideIcon } from 'lucide-react';

export type PathStepLink = {
  label: string;
  href: string;
  external?: boolean;
  soon?: boolean;
};

export type PathStep = {
  step: number;
  title: string;
  description: string;
  icon: LucideIcon;
  links: PathStepLink[];
  shareAction?: boolean;
};

type PathStepsProps = {
  steps: PathStep[];
  onShare?: () => void;
  /** Apply shows three steps immediately; Classics reveals on scroll. */
  appear?: 'inView' | 'mount';
};

/**
 * Numbered onboarding path used on Classics and /apply (KYC steps 1–3).
 */
const PathSteps = ({ steps, onShare, appear = 'inView' }: PathStepsProps) => {
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {steps.map((item, index) => {
        const Icon = item.icon;
        const motionProps =
          appear === 'mount'
            ? {
                initial: { opacity: 0, y: 16 },
                animate: { opacity: 1, y: 0 },
              }
            : {
                initial: { opacity: 0, x: index % 2 === 0 ? -20 : 20 },
                whileInView: { opacity: 1, x: 0 },
                viewport: { once: true },
              };
        return (
          <motion.div
            key={item.step}
            {...motionProps}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
            data-sf-step={item.step}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">
                {item.step}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={20} className="text-primary flex-shrink-0" />
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                <div className="flex flex-wrap gap-2">
                  {item.links.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-primary-50 text-primary border border-primary/20 hover:bg-primary hover:text-white transition-colors"
                    >
                      {link.label}
                      {link.soon && (
                        <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold border border-red-400 text-red-500 rounded-full uppercase tracking-wide bg-white">
                          soon
                        </span>
                      )}
                      <ExternalLink size={12} className="ml-1.5 opacity-70" />
                    </a>
                  ))}
                  {item.shareAction && onShare && (
                    <button
                      type="button"
                      onClick={onShare}
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-secondary text-white hover:bg-secondary-600 transition-colors"
                    >
                      <Share2 size={14} className="mr-1.5" />
                      Share this page
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default PathSteps;
